import { BYTES_IN_TB, bytesToGb } from '@fd/common/biz/formatBytesToGb';
import useTrafficAvailable from '@/hooks/useTrafficAvailable';
import Flex from '../ui/Flex';
import Card from '../ui/Card';
import { NavLink } from 'react-router';
import ProxyIcon from '@/assets/icons/ProxyIcon';
import { useTrafficGbAvailableByProxyType } from '@/hooks/useTrafficAvailableByProxyType';
import { ProxyTypeMeta, ProxyTypeMetas } from '@fd/common/biz/proxyType';
import useActiveSbSubscription from '@/hooks/queries/useSbSubscription';
import useOrder from '@/hooks/queries/useOrder';
import { useMemo } from 'react';
import { InfoIcon } from 'lucide-react';
import { Button } from '@fd/frontend/components/ui/Button';

export function AvailableTrafficCard() {
  const trafficAvailable = useTrafficAvailable();
  const { data: subscription } = useActiveSbSubscription();
  const orders = useOrder();

  const expirationDate = useMemo(() => {
    if (subscription?.paid_until) {
      return new Date(subscription.paid_until);
    } else if (orders?.length > 0 && orders[0].paid_until) {
      return new Date(orders[0].paid_until);
    }
    return null;
  }, [subscription, orders]);

  return (
    <Card
      className="w-full sm:max-w-[320px] p-4"
      isLoading={trafficAvailable === undefined}
    >
      <Flex.Col className="h-full gap-2.5">
        <Flex.Row className="justify-between">
          <Flex.Col className="gap-1 justify-between">
            <div className="text-sm">Available:</div>
            <div className="text-[28px]/7 font-semibold text-nowrap">
              {bytesToGb(trafficAvailable!).toFixed(
                trafficAvailable! < BYTES_IN_TB ? 1 : 0
              )}{' '}
              GB
            </div>
          </Flex.Col>
          <Flex.Col className="gap-2 justify-between">
            {expirationDate && (
              <>
                <div className="text-sm">Expiration date:</div>
                <div className="font-semibold">
                  {expirationDate?.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </div>
              </>
            )}
          </Flex.Col>
        </Flex.Row>
        <div></div>
        <NavLink to="/proxies/buy">
          <Button variant="secondary" className='w-full'>Buy more</Button>
        </NavLink>
        <Flex.Col className="gap-3 justify-center h-full">
          {trafficAvailable === 0 && orders.length > 0 ? (
            <NavLink to="/proxies/buy">
              <Card className="w-full h-36 bg-pink-100 border-rose-200 hover:border-rose-300">
                <Flex.Col className="gap-2 h-full">
                  <InfoIcon className="stroke-rose-500" />
                  <span>
                    All limits have been exceeded, purchase more traffic
                  </span>
                </Flex.Col>
              </Card>
            </NavLink>
          ) : (
            <>
              <AvailableTrafficLine proxy={ProxyTypeMetas.residential} />
              <AvailableTrafficLine proxy={ProxyTypeMetas.mobile} />
              <AvailableTrafficLine proxy={ProxyTypeMetas.datacenter} />
            </>
          )}
        </Flex.Col>
      </Flex.Col>
    </Card>
  );
}

function AvailableTrafficLine({ proxy }: { proxy: ProxyTypeMeta }) {
  const trafficGb = useTrafficGbAvailableByProxyType(proxy.name);
  return (
    <Flex.Row className="justify-between items-center p-2 bg-primary-background rounded-lg">
      <Flex.Row className="gap-3 items-center">
        <div className="bg-proxy-icon-bg p-1.5 rounded-lg flex items-center justify-center h-8 w-8">
          <ProxyIcon type={proxy.name} className="stroke-white h-5 w-5" />
        </div>
        <div className="text-sm">{proxy.title}</div>
      </Flex.Row>
      <div className="text-sm font-semibold">{trafficGb!.toFixed(1)} GB</div>
    </Flex.Row>
  );
}
